import React from 'react';
import { format } from 'date-fns';
import { formatTime, getCheckoutTime } from '../../../utils/job-utils';
import JobBadges from '../indicators/JobBadges';
import { 
  AreaIndicator
} from '../../../components/job-indicators';
import DueByDisplay from '../../../components/DueByDisplay';
import { getPropertyManagementLabel } from '../../../utils/propertyManagers';

export default function JobCardAssigned({ 
  job,
  onEdit
}) {
  // Get service type for styling
  const serviceType = job.serviceType;
  
  // Check if this is a flip job
  const isFlipJob = job.isFlip;
  
  // Create dynamic styles for service type color border
  const cardStyle = serviceType?.color ? {
    borderLeftWidth: '8px',
    borderLeftColor: serviceType.color
  } : {};

  // Determine background and border classes for flip jobs
  const bgColorClass = 'bg-white'; // Keep standard background
  const borderClass = isFlipJob ? 'border border-red-200' : 'border border-gray-200';

  return (
    <div className="p-4">
      <div 
        className={`grid grid-cols-[3fr_2fr] gap-4 p-4 rounded-lg shadow-sm ${borderClass} ${bgColorClass}`}
        style={cardStyle}
      >
        {/* Left Column - 3/5 width */}
        <div className="flex flex-col">
          {/* Job title at top */}
          <h4 className="font-medium text-gray-900 mb-1">{job.property.name}</h4>
          
          {/* Indicators below title */}
          <div className="mb-2 flex flex-row-reverse gap-1 whitespace-nowrap justify-start">
            <JobBadges job={job} allJobs={[]} />
          </div>
          <p className="text-xs text-gray-500 mb-1">
            {format(new Date(job.scheduledDate), 'MMM d, yyyy')}
            {getPropertyManagementLabel(job.property) && (
              <span className="text-gray-400">
                {' • PM: '}
                <span className="text-gray-600">{getPropertyManagementLabel(job.property)}</span>
              </span>
            )}
          </p>
          <p className="text-sm text-gray-500 mb-1">{job.property.propertyCode}</p>
          {/* Show different time info based on job type */}
          {job.serviceType?.timeBase === 'CUSTOM' ? (
            /* For custom jobs, show Start Time */
            job.startTime && (
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <div className="text-sm mr-1 font-medium text-gray-600">
                    Start Time:
                  </div>
                  <div className="text-sm text-gray-800 font-semibold">{formatTime(job.startTime)}</div>
                </div>
              </div>
            )
          ) : (
            /* For reservation-based jobs, show Guest Checkout */
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <div className="text-sm mr-1 font-medium text-gray-600">
                  Guest Checkout:
                </div>
                {job.hasTimeOverride ? (
                  <div className="flex flex-col text-sm">
                    {getCheckoutTime(job) && (
                      <span className="line-through text-gray-400">{formatTime(getCheckoutTime(job))}</span>
                    )}
                    <span className="flex items-center text-sky-600 font-semibold">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                      </svg>
                      {formatTime(job.startTime)}
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-800 font-semibold">{formatTime(getCheckoutTime(job))}</div>
                )}
              </div>
            </div>
          )}
          {/* Show "Due by" for all job types */}
          <div className="text-sm mt-1 bg-blue-50 p-1 rounded">
            <span className="inline-flex items-center">
              <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-600 font-medium mr-1">Due by:</span>
              <span className="text-blue-700 font-semibold"><DueByDisplay job={job} /></span>
            </span>
          </div>
        </div>

        {/* Right Column - 2/5 width */}
        <div className="flex flex-col justify-end">
          {/* Bottom area and edit button */}
          <div className="flex justify-end items-center gap-2">
            {job.property.area && <AreaIndicator area={job.property.area} />}
            <button
              onClick={() => onEdit(job)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 